




import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useSelector } from "react-redux";
import { AgGridReact } from "ag-grid-react";
import BranchWiseCumulative from "./BranchWiseCumulative";
import {
  ColDef,
  ColGroupDef,
  ModuleRegistry,
  AllCommunityModule,
  RowStyle,
  CellStyle,
} from "ag-grid-community";
import { RootState } from "../../../redux/store";
import useFetch from "../../../hooks/useFetch";
import ExportButton from "../pages/Exportbutton";

ModuleRegistry.registerModules([AllCommunityModule]);

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────

export interface IGWMonthRow {
  Month: string;
  Bold: number;
  CYAmount: number;
  LYAmount: number;
  GrowthPer: number;
}

export interface IGWRawRow {
  Particular: string;
  MasterType: number;
  Expend: number;
  Children: any[];
  DataM: IGWMonthRow[];
}

interface TreeNode {
  particular: string;
  masterType: number;
  expend: number;
  dataM: IGWMonthRow[];
  children: TreeNode[];
  isExpanded: boolean;
  isLoading: boolean;
  level: number;
  nodeKey: string;
}

interface AccountGroupWiseTableProps {
  heading: string;
  VchType: "9" | "2";
  yr1Label?: string;
  yr2Label?: string;
  isCumulative?: boolean;
}

type FilterMode = "all" | "monthly" | "quarterly";

// ─────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────

const formatAmount = (val: number) =>
  Number(val || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const formatGrowth = (val: number) => {
  const num = Number(val || 0);
  return `${num > 0 ? "+" : ""}${num.toFixed(2)}%`;
};

const QUARTER_INDICES = new Set([3, 7, 11, 15]);

const STATIC_MONTHS = [
  { name: "April",     isQ: false, idx: 0  },
  { name: "May",       isQ: false, idx: 1  },
  { name: "June",      isQ: false, idx: 2  },
  { name: "Quarter 1", isQ: true,  idx: 3  },
  { name: "July",      isQ: false, idx: 4  },
  { name: "August",    isQ: false, idx: 5  },
  { name: "September", isQ: false, idx: 6  },
  { name: "Quarter 2", isQ: true,  idx: 7  },
  { name: "October",   isQ: false, idx: 8  },
  { name: "November",  isQ: false, idx: 9  },
  { name: "December",  isQ: false, idx: 10 },
  { name: "Quarter 3", isQ: true,  idx: 11 },
  { name: "January",   isQ: false, idx: 12 },
  { name: "February",  isQ: false, idx: 13 },
  { name: "March",     isQ: false, idx: 14 },
  { name: "Quarter 4", isQ: true,  idx: 15 },
];

// ─────────────────────────────────────────────
// TREE UTILITIES
// ─────────────────────────────────────────────

const toTreeNodes = (
  rawItems: IGWRawRow[],
  level: number,
  parentKey: string
): TreeNode[] =>
  rawItems.map((item, i) => ({
    particular: item.Particular,
    masterType: item.MasterType,
    expend: item.MasterType == 1 && item.Expend == 0 ? 1 : 0,
    dataM: item.DataM || [],
    children: [],
    isExpanded: false,
    isLoading: false,
    level,
    nodeKey: `${parentKey}__${item.Particular}_${i}`,
  }));

const updateNodeInTree = (
  nodes: TreeNode[],
  targetKey: string,
  updater: (node: TreeNode) => TreeNode
): TreeNode[] =>
  nodes.map((node) => {
    if (node.nodeKey === targetKey) return updater(node);
    if (node.children.length > 0)
      return { ...node, children: updateNodeInTree(node.children, targetKey, updater) };
    return node;
  });

// const flattenTree = (nodes: TreeNode[]): TreeNode[] => {
//   const result: TreeNode[] = [];
//   const walk = (list: TreeNode[]) => {
//     list.forEach((n) => {
//       result.push(n);
//       if (n.isExpanded && n.children.length > 0) walk(n.children);
//     });
//   };
//   walk(nodes);
//   return result;
// };


const flattenTree = (nodes: TreeNode[]): TreeNode[] => {
  const result: TreeNode[] = [];
  const walk = (list: TreeNode[]) => {
    list.forEach((n) => {
      result.push(n);
      // ← ADD THIS: loading placeholder row
      if (n.isLoading) {
        result.push({
          particular: "__LOADING__",
          masterType: 0,
          expend: 0,
          dataM: [],
          children: [],
          isExpanded: false,
          isLoading: false,
          level: n.level + 1,
          nodeKey: `${n.nodeKey}__loading_placeholder`,
        });
      }
      if (n.isExpanded && n.children.length > 0) walk(n.children);
    });
  };
  walk(nodes);
  return result;
};


// ─────────────────────────────────────────────
// ISOLATED GRID WRAPPER
// Strict Mode double-invoke se bachane ke liye
// AgGridReact ko alag component mein wrap karo
// ─────────────────────────────────────────────

interface GridWrapperProps {
  rowData: TreeNode[];
  columnDefs: (ColDef | ColGroupDef)[];
  pinnedBottomRowData: any[];
  getRowStyle: (params: any) => RowStyle | undefined;
}

const GridWrapper: React.FC<GridWrapperProps> = ({
  rowData,
  columnDefs,
  pinnedBottomRowData,
  getRowStyle,
}) => {
  return (
    <AgGridReact
      rowData={rowData}
      columnDefs={columnDefs}
      theme="legacy"
      headerHeight={32}
      groupHeaderHeight={34}
      rowHeight={40}
      suppressMovableColumns={true}
      suppressColumnVirtualisation={true}
      defaultColDef={{
        sortable: false,
        resizable: true,
        suppressHeaderMenuButton: true,
      }}
      pinnedBottomRowData={pinnedBottomRowData}
      getRowStyle={getRowStyle}
    />
  );
};

// React.memo so it doesn't re-render unless props actually change
const MemoGridWrapper = React.memo(GridWrapper);

// ─────────────────────────────────────────────
// MOBILE CARD
// ─────────────────────────────────────────────

interface MobileNodeCardProps {
  node: TreeNode;
  yr1Label: string;
  yr2Label: string;
  onExpand: (node: TreeNode) => void;
  visibleMonths: typeof STATIC_MONTHS;
}

const MobileNodeCard: React.FC<MobileNodeCardProps> = ({
  node,
  yr1Label,
  yr2Label,
  onExpand,
  visibleMonths,
}) => {
  const indent = node.level * 14;
  const isExpandable = node.expend === 1;

  return (
    <div
      style={{
        marginLeft: `${indent}px`,
        marginBottom: "8px",
        borderRadius: "10px",
        border: `1.5px solid ${node.level === 0 ? "#94a3b8" : "#e2e8f0"}`,
        borderLeft: `4px solid ${
          node.level === 0 ? "#0f4c81" : node.level === 1 ? "#3b82f6" : node.level === 2 ? "#10b981" : "#f59e0b"
        }`,
        background: node.level === 0 ? "#f0f4ff" : "#ffffff",
        overflow: "hidden",
      }}
    >
      <div
        onClick={() => isExpandable && onExpand(node)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          padding: "10px 12px",
          cursor: isExpandable ? "pointer" : "default",
          background: node.level === 0 ? "#e8edf8" : "transparent",
          borderBottom: "1px solid #e2e8f0",
        }}
      >
        {isExpandable ? (
          <span
            style={{
              width: "20px", height: "20px", borderRadius: "4px",
              background: "#0f4c81", color: "#fff", display: "flex",
              alignItems: "center", justifyContent: "center", fontSize: "10px", flexShrink: 0,
            }}
          >
            {node.isLoading ? "⟳" : node.isExpanded ? "▼" : "▶"}
          </span>
        ) : (
          <span
            style={{
              width: "20px", height: "20px", borderRadius: "50%",
              background: "#e2e8f0", display: "flex", alignItems: "center",
              justifyContent: "center", fontSize: "8px", color: "#94a3b8", flexShrink: 0,
            }}
          >
            ●
          </span>
        )}
        <span style={{ fontWeight: node.level === 0 ? 700 : 600, fontSize: "13px", color: "#0f172a" }}>
          {node.particular}
        </span>
        {node.level > 0 && (
          <span style={{ marginLeft: "auto", fontSize: "9px", color: "#94a3b8", background: "#f1f5f9", padding: "2px 6px", borderRadius: "4px" }}>
            L{node.level + 1}
          </span>
        )}
      </div>

      <div style={{ padding: "10px 12px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
        {visibleMonths.map((m) => {
          const mData = node.dataM?.[m.idx];
          if (!mData) return null;
          const growth = mData.GrowthPer || 0;
          const growthColor = growth > 0 ? "#059669" : growth < 0 ? "#dc2626" : "#94a3b8";
          const growthBg = growth > 0 ? "#dcfce7" : growth < 0 ? "#fee2e2" : "#f1f5f9";

          return (
            <div
              key={m.idx}
              style={{
                flex: "1 1 calc(50% - 8px)", minWidth: "130px", borderRadius: "8px",
                padding: "8px", background: m.isQ ? "#fffbeb" : "#f8fafc",
                border: `1px solid ${m.isQ ? "#f59e0b" : "#e2e8f0"}`,
              }}
            >
              <div style={{ fontSize: "10px", fontWeight: 700, color: m.isQ ? "#92400e" : "#475569", marginBottom: "6px", textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
                {m.name}
                {m.isQ && <span style={{ background: "#b45309", color: "#fff", fontSize: "8px", padding: "1px 4px", borderRadius: "3px" }}>Q</span>}
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "3px" }}>
                <span style={{ color: "#64748b" }}>FY {yr1Label}</span>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{formatAmount(mData.LYAmount)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", marginBottom: "3px" }}>
                <span style={{ color: "#64748b" }}>FY {yr2Label}</span>
                <span style={{ fontWeight: 600, color: "#0f4c81" }}>{formatAmount(mData.CYAmount)}</span>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: "11px", background: growthBg, borderRadius: "4px", padding: "3px 5px" }}>
                <span style={{ color: "#64748b" }}>Growth</span>
                <span style={{ fontWeight: 700, color: growthColor }}>{formatGrowth(growth)}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

const AccountGroupWiseTable: React.FC<AccountGroupWiseTableProps> = ({
  heading,
  VchType,
  yr1Label = "",
  yr2Label = "",
  isCumulative = false,
}) => {
  const callFetch = useFetch();
  const { decryptedData } = useSelector((state: RootState) => state.authData);
  const { CompCode, FinYear, ID } = decryptedData || {};

  const [rawData, setRawData] = useState<IGWRawRow[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [initialLoading, setInitialLoading] = useState(false);
  const [filterMode, setFilterMode] = useState<FilterMode>("all");

  // ── gridInstanceKey: sirf yahi control karega grid mount/unmount ──
  // Har baar filter change hone par naya unique key milega
  const [gridInstanceKey, setGridInstanceKey] = useState<string>(() => `${VchType}_all_0`);
  const filterCounterRef = useRef(0);

  // ── Fetch API ────────────────────────────────────
  const fetchIGWData = useCallback(
    async (ParentGrp: string): Promise<IGWRawRow[]> => {
      try {
        const queryParams = new URLSearchParams({
          CompCode: String(CompCode),
          FY: String(FinYear),
          VchType,
          ParentGrp,
          UserID: String(ID || 0),
          Cumulative: String(isCumulative ? 1 : 0),
        }).toString();

        const url = `Report/GetDashboardComparitiveDataAGW?${queryParams}`;
        const { got }: { got: any } = await callFetch(url, "GET");

        if (got?.Status === 1 && Array.isArray(got.Data)) {
          return got.Data;
        }
        return [];
      } catch (err) {
        console.error("AccountGroupWise fetch error =>", err);
        return [];
      }
    },
    [callFetch, CompCode, FinYear, VchType, ID, isCumulative]
  );

  // ── Initial Load ─────────────────────────────────
  useEffect(() => {
    if (!CompCode || !FinYear) return;
    const loadRoot = async () => {
      setInitialLoading(true);
      const raw = await fetchIGWData("");
      setRawData(raw);
      const nodes = toTreeNodes(raw, 0, "root");
      setTreeData(nodes);
      setInitialLoading(false);
    };
    loadRoot();
  }, [CompCode, FinYear, VchType, isCumulative]);

  // ── Filter Mode Handler ───────────────────────────
  // Grid ko completely destroy+recreate karna hoga
  const handleFilterMode = useCallback(
    (mode: FilterMode) => {
      if (mode === filterMode) return;
      // Pehle grid hatao
      setGridInstanceKey(""); // empty = grid unmount
      setFilterMode(mode);
      // Next tick mein naya key lagao = fresh grid mount
      setTimeout(() => {
        filterCounterRef.current += 1;
        setGridInstanceKey(`${VchType}_${mode}_${filterCounterRef.current}`);
      }, 50);
    },
    [filterMode, VchType]
  );

  // ── Expand Handler ───────────────────────────────
  const handleExpand = useCallback(
    async (node: TreeNode) => {
      if (node.isExpanded) {
        setTreeData((prev) =>
          updateNodeInTree(prev, node.nodeKey, (n) => ({ ...n, isExpanded: false }))
        );
        return;
      }

      if (node.children.length > 0) {
        setTreeData((prev) =>
          updateNodeInTree(prev, node.nodeKey, (n) => ({ ...n, isExpanded: true }))
        );
        return;
      }

      setTreeData((prev) =>
        updateNodeInTree(prev, node.nodeKey, (n) => ({ ...n, isLoading: true }))
      );

      const childrenRaw = await fetchIGWData(node.particular);
      const childNodes = toTreeNodes(childrenRaw, node.level + 1, node.nodeKey);

      setTreeData((prev) =>
        updateNodeInTree(prev, node.nodeKey, (n) => ({
          ...n,
          isLoading: false,
          isExpanded: true,
          children: childNodes,
        }))
      );
    },
    [fetchIGWData]
  );

  // ── Flat rows for AG Grid ─────────────────────────
  const flatRows = useMemo(() => flattenTree(treeData), [treeData]);

  // ── Visible months based on filter ───────────────
  const visibleMonths = useMemo(() => {
    if (filterMode === "monthly") return STATIC_MONTHS.filter((m) => !m.isQ);
    if (filterMode === "quarterly") return STATIC_MONTHS.filter((m) => m.isQ);
    return STATIC_MONTHS;
  }, [filterMode]);

  // ── Grand Total (pinned bottom) ───────────────────
  const pinnedBottomRowData = useMemo(() => {
    if (!treeData.length) return [];

    const totalRow: any = {
      particular: "GRAND TOTAL", level: -1, expend: 0, dataM: [],
      children: [], isExpanded: false, isLoading: false, masterType: 0, nodeKey: "grand_total",
    };

    STATIC_MONTHS.forEach((m) => {
      let totalLY = 0, totalCY = 0;
      treeData.forEach((node) => {
        const mData = node.dataM?.[m.idx];
        if (mData) { totalLY += mData.LYAmount || 0; totalCY += mData.CYAmount || 0; }
      });
      const totalGrowth =
        totalLY !== 0 ? ((totalCY - totalLY) / Math.abs(totalLY)) * 100 : totalCY !== 0 ? 100 : 0;
      totalRow.dataM.push({ Month: m.name, Bold: m.isQ ? 1 : 0, LYAmount: totalLY, CYAmount: totalCY, GrowthPer: totalGrowth });
    });

    return [totalRow];
  }, [treeData]);

  // ── Column Defs ───────────────────────────────────
  // gridInstanceKey ko dependency mein rakho taki har fresh mount pe naye colIds milein
  const columnDefs = useMemo((): (ColDef | ColGroupDef)[] => {
    // unique prefix har grid instance ke liye
    const prefix = gridInstanceKey || `${VchType}_${filterMode}`;

    const baseDefs: (ColDef | ColGroupDef)[] = [
      {
        field: "particular",
        headerName: "Account Group",
        pinned: "left",
        width: 230,
        suppressMovable: true,
        headerClass: "igw-pinned-header",
        cellRenderer: (params: any) => {
          const node: TreeNode = params.data;
          if (!node) return null;

           if (node.particular === "__LOADING__") {
              return (
                <div style={{
                  paddingLeft: `${node.level * 18 + 8}px`,
                  display: "flex", alignItems: "center", gap: "8px",
                  height: "100%", color: "#64748b",
                }}>
                  <div
                    style={{
                      width: "14px", height: "14px", borderRadius: "50%",
                      border: "2px solid #3b82f6", borderTopColor: "transparent",
                      animation: "igw-spin 0.7s linear infinite", flexShrink: 0,
                    }}
                  />
                  <span style={{ fontSize: "12px", fontStyle: "italic" }}>Loading...</span>
                </div>
              );
            }
          const indent = Math.max(0, node.level) * 18;
          const isExpandable = node.expend === 1;
          const isGrandTotal = node.nodeKey === "grand_total";

          if (isGrandTotal) {
            return (
              <div style={{ paddingLeft: "12px", display: "flex", alignItems: "center", gap: "6px" }}>
                <span style={{ background: "rgba(255,255,255,0.2)", borderRadius: "4px", padding: "2px 6px", fontSize: "10px", color: "#fff", fontWeight: 700 }}>
                  TOTAL
                </span>
              </div>
            );
          }

          return (
            <div
              style={{
                paddingLeft: `${indent + 8}px`, display: "flex", alignItems: "center",
                gap: "7px", cursor: isExpandable ? "pointer" : "default", height: "100%",
              }}
              onClick={() => isExpandable && handleExpand(node)}
            >
              {isExpandable ? (
                <span
                  style={{
                    width: "18px", height: "18px", borderRadius: "4px",
                    background: node.isExpanded ? "#0f4c81" : "#e8edf4",
                    color: node.isExpanded ? "#fff" : "#0f4c81",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: "9px", flexShrink: 0, transition: "all 0.15s ease",
                    border: "1px solid #cbd5e1",
                  }}
                >
                  {node.isLoading ? "⟳" : node.isExpanded ? "▼" : "▶"}
                </span>
              ) : (
                <span
                  style={{
                    width: "8px", height: "8px", borderRadius: "50%",
                    background: node.level === 0 ? "#0f4c81" : node.level === 1 ? "#3b82f6" : node.level === 2 ? "#10b981" : "#f59e0b",
                    flexShrink: 0, marginLeft: "5px",
                  }}
                />
              )}
              <span
                style={{
                  fontWeight: node.level === 0 ? 700 : node.level === 1 ? 600 : 500,
                  fontSize: node.level === 0 ? "13px" : "12px",
                  color: node.level === 0 ? "#0f172a" : "#334155",
                  whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}
                title={node.particular}
              >
                {node.particular}
              </span>
              {/* {node.level > 0 && (
                <span style={{ marginLeft: "auto", fontSize: "9px", color: "#94a3b8", background: "#f1f5f9", padding: "1px 5px", borderRadius: "3px", flexShrink: 0 }}>
                  L{node.level + 1}
                </span>
              )} */}
            </div>
          );
        },
        cellStyle: (params: any): CellStyle => {
          const node: TreeNode = params.data;
          const isGrandTotal = node?.nodeKey === "grand_total";
          if (isGrandTotal) {
            return { background: "#1e293b", color: "#fff", fontWeight: 700, borderRight: "2px solid #475569" };
          }
          return {
            background: node?.level === 0 ? "#f8fafc" : "#ffffff",
            color: "#1e293b", fontWeight: "600", borderRight: "2px solid #475569",
          };
        },
      },
    ];

    // Month columns — prefix se har colId/groupId unique hoga
    visibleMonths.forEach((m) => {
      const keyStr = m.name.toLowerCase().replace(/\s+/g, "_");
      const groupSuffixClass = m.isQ
        ? "igw-quarter-group-header igw-month-block-border"
        : "igw-month-group-header igw-month-block-border";

      baseDefs.push({
        groupId: `${prefix}_grp_${keyStr}`,
        headerName: m.name,
        headerClass: `${groupSuffixClass} igw-centered-group`,
        children: [
          {
            colId: `${prefix}_ly_${keyStr}`,
            headerName: yr1Label || "LY",
            width: 110,
            suppressMovable: true,
            headerClass: "igw-sub-label",
            valueGetter: (p: any) => p.data?.dataM?.[m.idx]?.LYAmount,
            valueFormatter: (p: any) => formatAmount(p.value),
            cellStyle: (p: any) => {
              const isGrandTotal = p.data?.nodeKey === "grand_total";
              return {
                textAlign: "right",
                fontWeight: m.isQ ? "700" : p.data?.level === 0 ? "600" : "400",
                background: isGrandTotal ? "#1e293b" : m.isQ ? "#f1f5f9" : "transparent",
                color: isGrandTotal ? "#fff" : "#1e293b",
              };
            },
          },
          {
            colId: `${prefix}_cy_${keyStr}`,
            headerName: yr2Label || "CY",
            width: 110,
            suppressMovable: true,
            headerClass: "igw-sub-label",
            valueGetter: (p: any) => p.data?.dataM?.[m.idx]?.CYAmount,
            valueFormatter: (p: any) => formatAmount(p.value),
            cellStyle: (p: any) => {
              const isGrandTotal = p.data?.nodeKey === "grand_total";
              return {
                textAlign: "right",
                fontWeight: m.isQ ? "700" : p.data?.level === 0 ? "600" : "400",
                background: isGrandTotal ? "#1e293b" : m.isQ ? "#f1f5f9" : "transparent",
                color: isGrandTotal ? "#fff" : "#1e293b",
              };
            },
          },
          {
            colId: `${prefix}_gr_${keyStr}`,
            headerName: "Growth %",
            width: 105,
            suppressMovable: true,
            headerClass: "igw-sub-label igw-growth-label",
            cellClass: "igw-month-block-border",
            valueGetter: (p: any) => p.data?.dataM?.[m.idx]?.GrowthPer,
            valueFormatter: (p: any) => formatGrowth(p.value),
            cellStyle: (p: any) => {
              const val = Number(p.value || 0);
              const isGrandTotal = p.data?.nodeKey === "grand_total";
              return {
                textAlign: "right", fontWeight: "700",
                color: isGrandTotal
                  ? val > 0 ? "#86efac" : val < 0 ? "#fca5a5" : "#cbd5e1"
                  : val > 0 ? "#059669" : val < 0 ? "#dc2626" : "#94a3b8",
                background: isGrandTotal ? "#1e293b" : m.isQ ? "#e2e8f0" : "transparent",
              };
            },
          },
        ],
      });
    });

    // Annual Total group
    baseDefs.push({
      groupId: `${prefix}_annual`,
      headerName: "Annual Total",
      headerClass: "igw-annual-group-header igw-centered-group",
      children: [
        {
          colId: `${prefix}_annual_ly`,
          headerName: yr1Label || "LY",
          width: 120,
          suppressMovable: true,
          headerClass: "igw-sub-label",
          valueGetter: (p: any) => {
            if (!p.data?.dataM) return 0;
            return p.data.dataM.reduce(
              (sum: number, m: IGWMonthRow, idx: number) =>
                !QUARTER_INDICES.has(idx) ? sum + (m.LYAmount || 0) : sum, 0
            );
          },
          valueFormatter: (p: any) => formatAmount(p.value),
          cellStyle: (p: any) => {
            const isGrandTotal = p.data?.nodeKey === "grand_total";
            return {
              textAlign: "right", fontWeight: "700",
              background: isGrandTotal ? "#1e293b" : "#fffbeb",
              color: isGrandTotal ? "#fff" : "#92400e",
            };
          },
        },
        {
          colId: `${prefix}_annual_cy`,
          headerName: yr2Label || "CY",
          width: 120,
          suppressMovable: true,
          headerClass: "igw-sub-label",
          valueGetter: (p: any) => {
            if (!p.data?.dataM) return 0;
            return p.data.dataM.reduce(
              (sum: number, m: IGWMonthRow, idx: number) =>
                !QUARTER_INDICES.has(idx) ? sum + (m.CYAmount || 0) : sum, 0
            );
          },
          valueFormatter: (p: any) => formatAmount(p.value),
          cellStyle: (p: any) => {
            const isGrandTotal = p.data?.nodeKey === "grand_total";
            return {
              textAlign: "right", fontWeight: "700",
              background: isGrandTotal ? "#1e293b" : "#fffbeb",
              color: isGrandTotal ? "#fff" : "#92400e",
            };
          },
        },
        {
          colId: `${prefix}_annual_gr`,
          headerName: "Growth %",
          width: 110,
          suppressMovable: true,
          headerClass: "igw-sub-label igw-growth-label",
          cellClass: "igw-month-block-border",
          valueGetter: (p: any) => {
            if (!p.data?.dataM) return 0;
            let ly = 0, cy = 0;
            p.data.dataM.forEach((m: IGWMonthRow, idx: number) => {
              if (!QUARTER_INDICES.has(idx)) { ly += m.LYAmount || 0; cy += m.CYAmount || 0; }
            });
            return ly !== 0 ? ((cy - ly) / Math.abs(ly)) * 100 : cy !== 0 ? 100 : 0;
          },
          valueFormatter: (p: any) => formatGrowth(p.value),
          cellStyle: (p: any) => {
            const val = Number(p.value || 0);
            const isGrandTotal = p.data?.nodeKey === "grand_total";
            return {
              textAlign: "right", fontWeight: "800",
              color: isGrandTotal
                ? val > 0 ? "#86efac" : val < 0 ? "#fca5a5" : "#cbd5e1"
                : val > 0 ? "#059669" : val < 0 ? "#dc2626" : "#94a3b8",
              background: isGrandTotal ? "#1e293b" : "#fef9c3",
            };
          },
        },
      ],
    });

    return baseDefs;
  }, [visibleMonths, yr1Label, yr2Label, handleExpand, gridInstanceKey]);

  // ── Row style ─────────────────────────────────────
  const getRowStyle = useCallback((params: any): RowStyle | undefined => {
    const node: TreeNode = params.data;
    if (!node) return undefined;
      if (node.particular === "__LOADING__") {
      return { background: "#f0f9ff", borderBottom: "1px solid #bfdbfe" };
      }

    if (params.node.rowPinned === "bottom") {
      return { background: "#1e293b", fontWeight: "700" };
    }
    if (node.level === 0) return { background: "#f0f4ff", borderBottom: "1px solid #c7d2fe" };
    if (node.level === 1) return { background: "#fafafa" };
    return undefined;
  }, []);

  // ── Show grid only when key is set ───────────────
  // const showGrid = !initialLoading && gridInstanceKey !== "";

  // ─────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────

  if (isCumulative) {
    return (
      <BranchWiseCumulative
        heading={heading}
        rowData={rawData}
        yr1Label={yr1Label}
        yr2Label={yr2Label}
      />
    );
  }

  return (
    <>
      <style>{`
        .igw-grid .ag-root-wrapper { border: 1px solid #cbd5e1 !important; border-radius: 8px !important; overflow: hidden; }
        .igw-grid .ag-header { background-color: #1e293b !important; }
        .igw-grid .ag-header-row { background-color: #1e293b !important; }
        .igw-grid .igw-centered-group .ag-header-group-cell-label { justify-content: center !important; text-align: center !important; width: 100%; }
        .igw-grid .ag-header-group-cell { background-color: #0f4c81 !important; color: #fff !important; font-weight: 700 !important; font-size: 13px !important; border-bottom: 1px solid #1e293b !important; }
        .igw-grid .igw-quarter-group-header { background-color: #334155 !important; }
        .igw-grid .igw-annual-group-header { background-color: #92400e !important; }
        .igw-grid .igw-sub-label .ag-header-cell-label { justify-content: center !important; }
        .igw-grid .igw-pinned-header .ag-header-cell-label { justify-content: flex-start !important;  padding-left: 10px !important; }
        .igw-grid .ag-header-cell-text { color: #fff !important; font-weight: 600 !important; font-size: 12px !important; }
        .igw-grid .igw-month-block-border { border-right: 1px solid #000 !important; }
        .igw-grid .ag-header-group-cell.igw-month-block-border { border-right: 1px solid #000 !important; }
        .igw-grid .ag-row { border-bottom: 1px solid #e2e8f0 !important; }
        .igw-grid .ag-row-odd { background-color: #f8fafc; }
        .igw-grid .ag-cell { display: flex; align-items: center; padding: 0 10px; border-right: 1px solid #e2e8f0 !important; justify-content: flex-end !important; }
        .igw-grid .ag-cell[col-id$="_ly_april"],.igw-grid .ag-cell[col-id$="_ly_may"] { justify-content: flex-end !important; }
        .igw-grid .ag-cell[col-id="particular"] { justify-content: flex-start !important; padding: 0 !important; }
        .igw-grid .ag-row:hover { background-color: #dbeafe !important; }
        .igw-grid .ag-row-pinned .ag-cell { border-top: 2px solid #475569 !important; }

        .igw-toolbar {
          display: flex; align-items: center; padding: 14px 18px;
          background: linear-gradient(135deg, #0f172a 0%, #1e3a5f 60%, #0f4c81 100%);
          border-bottom: 1px solid #1e3a5f; flex-wrap: wrap; gap: 10px;
        }
        .igw-toolbar-title {
          margin: 0; font-size: 15px; font-weight: 700; color: #f0f9ff;
          display: flex; align-items: center; gap: 8px; flex: 1;
        }
        .igw-filter-group {
          display: flex; background: rgba(255,255,255,0.1); padding: 3px;
          border-radius: 9px; gap: 2px; border: 1px solid rgba(255,255,255,0.15);
        }
        .igw-filter-btn {
          padding: 5px 12px; border: none; border-radius: 7px; cursor: pointer;
          font-size: 11px; letter-spacing: 0.5px; transition: all 0.15s ease;
          color: rgba(255,255,255,0.7); background: transparent;
        }
        .igw-filter-btn:hover { background: rgba(255,255,255,0.15) !important; color: #fff !important; }
        .igw-filter-btn.active {
          background: #fff !important; color: #0f4c81 !important;
          font-weight: 700 !important; box-shadow: 0 2px 6px rgba(0,0,0,0.2);
        }
        .igw-legend {
          display: flex; align-items: center; gap: 14px; padding: 8px 18px;
          background: #f8fafc; border-bottom: 1px solid #e2e8f0; flex-wrap: wrap;
        }
        .igw-legend-item { display: flex; align-items: center; gap: 5px; font-size: 11px; color: #64748b; font-weight: 500; }
        .igw-desktop { display: block; }
        .igw-mobile { display: none; padding: 12px; background: #f0f4ff; }
        @media (max-width: 768px) {
          .igw-desktop { display: none !important; }
          .igw-mobile { display: block !important; }
        }
         @keyframes igw-spin {
            to { transform: rotate(360deg); }
          }
      `}</style>

      <div style={{ background: "#fff", borderRadius: "12px", border: "1px solid #e2e8f0", overflow: "hidden", boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)" }}>

        {/* ── TOOLBAR ── */}
        <div className="igw-toolbar">
          <h3 className="igw-toolbar-title">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="7" height="7" rx="1" />
              <rect x="14" y="3" width="7" height="7" rx="1" />
              <rect x="3" y="14" width="7" height="7" rx="1" />
              <path d="M17 17h.01M14 20h6M17 14v6" />
            </svg>
            <span>{heading}</span>
          </h3>

          <div className="igw-filter-group">
            {(["all", "monthly", "quarterly"] as FilterMode[]).map((mode) => (
              <button
                key={mode}
                onClick={() => handleFilterMode(mode)}
                className={`igw-filter-btn${filterMode === mode ? " active" : ""}`}
              >
                {mode.toUpperCase()}
              </button>
            ))}

              <span>
            <ExportButton variant="toolbar" params={{ type:"accountgroup", heading, rows:flatRows, yr1Label, yr2Label, filterMode }} />
          </span>
          </div>
        </div>

 


        {/* ── DESKTOP GRID ── */}
        <div className="igw-desktop">
          <div className="igw-grid" style={{ height: "520px", width: "100%", padding: "10px" }}>
            {initialLoading ? (
              <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "12px" }}>
                <div className="spinner-border text-primary opacity-50" role="status" />
                <p className="text-muted small fw-medium m-0">Loading {heading}...</p>
              </div>
            ) : !gridInstanceKey ? (
              // Filter switch hone par briefly dikhega
              <div style={{ height: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "10px" }}>
                <div className="spinner-border text-primary opacity-50" role="status" />
                <p className="text-muted small fw-medium m-0">Switching view...</p>
              </div>
            ) : (
              // key prop se grid completely destroy+recreate hoti hai
              // MemoGridWrapper Strict Mode ke double-invoke se safe hai
              <MemoGridWrapper
                key={gridInstanceKey}
                rowData={flatRows}
                columnDefs={columnDefs}
                pinnedBottomRowData={pinnedBottomRowData}
                getRowStyle={getRowStyle}
              />
            )}
          </div>
        </div>

        {/* ── MOBILE CANVAS ── */}
        <div className="igw-mobile">
          {initialLoading ? (
            <div style={{ textAlign: "center", padding: "40px", color: "#94a3b8" }}>Loading...</div>
          ) : (
           

            flatRows
              .filter(node => node.particular !== "__LOADING__" || node.isLoading)
              .map((node, idx) => {
                if (node.particular === "__LOADING__") {
                  return (
                    <div key={node.nodeKey} style={{
                      marginLeft: `${node.level * 14}px`,
                      padding: "10px 16px",
                      display: "flex", alignItems: "center", gap: "8px",
                      color: "#64748b", fontSize: "13px",
                      background: "#f0f9ff", borderRadius: "8px",
                      border: "1px dashed #93c5fd", marginBottom: "6px",
                    }}>
                      <div style={{
                        width: "14px", height: "14px", borderRadius: "50%",
                        border: "2px solid #3b82f6", borderTopColor: "transparent",
                        animation: "igw-spin 0.7s linear infinite",
                      }} />
                      Loading...
                    </div>
                  );
                }
                return (
                  <MobileNodeCard
                    key={node.nodeKey || idx}
                    node={node}
                    yr1Label={yr1Label}
                    yr2Label={yr2Label}
                    onExpand={handleExpand}
                    visibleMonths={visibleMonths}
                  />
                );
              })
          )}

          
        </div>
      </div>
    </>
  );
};

export default AccountGroupWiseTable;